import { AuthRightColumn } from '@/components/SlimHeader';
import type { PageExperience } from '@/features/experiences/types';
import { ShareModal } from '@/features/permissions/components/ShareModal';
import { isEditRole } from '@/features/permissions/lib/permissions.lib';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import { ExperienceTypes } from '@/types/gql.generated';
import { Box, Flex } from '@/ui';
import { CustomizeButton } from '../CustomizeMode';
import { useOnboardingAnchor } from '../PlannerOnboarding/anchorContext';
import { CollaboratorAvatars } from './CollaboratorAvatars';
import { SaveToLibraryButton } from './SaveToLibraryButton';

type Props = {
  experience: PageExperience;
  readOnly: boolean;
};

// The published view withholds every editing control, an owner's and an editor's
// included: PublicViewNotice's Edit link is the one route from here into the
// editor, so Share gates on `readOnly` alone rather than on the viewer's role.
export const HeaderActions = ({ experience, readOnly }: Props) => {
  const isPlanner = experience.type === ExperienceTypes.Planner;
  // Customize mode is hidden on mobile this iteration (deferred to a later pass).
  const isMobile = useIsMobileBreakpoint();
  const customizeAnchorRef = useOnboardingAnchor('customize');
  const showSave = readOnly && !isEditRole(experience.role);

  return (
    <Flex align="center" gap={{ base: 1.5, md: 3 }}>
      {!readOnly && <CollaboratorAvatars />}
      {showSave && <SaveToLibraryButton />}
      {!readOnly && (
        <ShareModal
          triggerProps={{
            size: 'ah',
            // The trigger's default borderRadius="full" would win over the
            // variant radius, leaving Share rounder than Customize.
            borderRadius: 'ah-lg',
            variant: 'ahOutline',
          }}
        />
      )}
      {!readOnly && isPlanner && !isMobile && (
        <Box ref={customizeAnchorRef}>
          <CustomizeButton />
        </Box>
      )}
      <AuthRightColumn buttonProps={{ width: { md: 'auto' } }} isCompact />
    </Flex>
  );
};
